<template>
  <div class="page-layout">
    <a-card :loading="loading" class="page-layout__card">
      <div slot="title" class="title-bar">
        <!-- 返回主页按钮 -->
        <a-tooltip placement="right">
          <template slot="title">
            <span>返回标记集列表</span>
          </template>
          <a-button class="btn-home" shape="circle" @click="handleClickHome">
            <i class="iconfont icon-home1"></i>
          </a-button>
        </a-tooltip>
        <!-- 标题 -->
        <div class="title-name">{{markerSet.name}}</div>
      </div>
      <!-- 分组标签栏 -->
      <a-tabs
        v-if="groupTabs.length > 1"
        class="group-tabs"
        :activeKey="activeGroupKey"
        @change="handleGroupTabChange"
        size="small"
        :tabBarGutter="0"
      >
        <a-tab-pane v-for="tab in groupTabs" :key="tab" :tab="tab" />
      </a-tabs>
      <!-- 点/面类型切换 -->
      <div class="type-filter" v-if="hasMixedTypes">
        <a-radio-group
          size="small"
          :value="typeFilter"
          @change="handleTypeFilterChange"
          class="type-filter__group"
        >
          <a-radio-button value="">点</a-radio-button>
          <a-radio-button value="polygon">面</a-radio-button>
        </a-radio-group>
      </div>
      <div class="marker-list">
        <!-- 点标记列表 -->
        <a-list
          v-show="typeFilter !== 'polygon'"
          class="marker-list__list"
          itemLayout="horizontal"
          :dataSource="filteredMarkerList"
        >
          <a-list-item slot="renderItem" slot-scope="item" @click="handleItemClick(item)">
            <a-list-item-meta
              :description="item.address">
              <div slot="title">
                <span class="marker-no">{{ item.displaySeq }}</span>
                {{ item.name }}
              </div>
            </a-list-item-meta>
            <span v-if="item.pictures && item.pictures.length" slot="actions">
              <i
                class="iconfont icon-tupian">
              </i>
              {{item.pictures.length}}
            </span>
            <a slot="actions" @click.stop="handleClickEdit(item)">详情</a>
            <a slot="actions" @click.stop>
              <a-popconfirm
                placement="right"
                okText="确定"
                cancelText="取消"
                @confirm="handleDeleteMarker(item)"
              >
                <template slot="title">
                  <p>{{ `确定删除标记 ${item.name} 吗？` }}</p>
                </template>
                <span>删除</span>
              </a-popconfirm>
            </a>
          </a-list-item>
        </a-list>
        <!-- 面标记列表 -->
        <a-list
          v-show="typeFilter === 'polygon'"
          class="marker-list__list"
          itemLayout="horizontal"
          :dataSource="filteredPolygonList"
        >
          <a-list-item slot="renderItem" slot-scope="item" @click="$emit('polygonHighlight', item)">
            <a-list-item-meta>
              <div slot="title">
                <span class="polygon-no">
                  <span class="polygon-no__inner">{{ item.displaySeq }}</span>
                </span>
                {{ item.name || '未命名' }}
              </div>
              <div slot="description">
                {{ item.points.length }} 个点
                <span
                  v-if="item._area"
                  class="area-toggle"
                  @click.stop="handleToggleAreaUnit"
                > · {{ item._area }}</span>
              </div>
            </a-list-item-meta>
            <a slot="actions" @click.stop="handleEditPolygon(item)">编辑</a>
            <a slot="actions" @click.stop>
              <a-popconfirm
                placement="right"
                okText="确定"
                cancelText="取消"
                @confirm="handleDeletePolygon(item)"
              >
                <template slot="title">
                  <p>{{ `确定删除面标记 ${item.name || '未命名'} 吗？` }}</p>
                </template>
                <span>删除</span>
              </a-popconfirm>
            </a>
          </a-list-item>
        </a-list>
      </div>
    </a-card>
    <div class="marker-list__tools">
      <a-dropdown>
        <a-button
          class="marker-tools__add"
          shape="circle"
          @click="handleClickAdd"
        >
          <i class="iconfont icon-icon-test"></i>
        </a-button>
        <a-menu slot="overlay">
          <a-menu-item @click="handleClickAdd">
            搜索添加
          </a-menu-item>
          <a-menu-item @click="handleRightClickAdd">
            快速添加
          </a-menu-item>
          <a-menu-item @click="handleLinkAdd">
            链接添加
          </a-menu-item>
          <a-menu-item @click="handleDrawPolygon()">
            添加面
          </a-menu-item>
        </a-menu>
      </a-dropdown>

      <a-tooltip placement="right">
        <template slot="title">
          <span>保存</span>
        </template>
        <a-button
          shape="circle"
          :loading="savingLoading"
          @click="handleSave">
          <i v-show="!savingLoading" class="iconfont icon-baocun"></i>
        </a-button>
      </a-tooltip>

      <a-tooltip placement="right">
        <template slot="title">
          <span>导入与导出</span>
        </template>
        <a-button
          shape="circle"
          @click="importExportModalVisible = true">
          <i class="iconfont icon-shangchuanxiazai"></i>
        </a-button>
      </a-tooltip>

      <a-tooltip placement="right">
        <template slot="title">
          <span>搜索标记</span>
        </template>
        <a-button
          shape="circle"
          @click="searchModalVisible = true">
          <i class="iconfont icon-sousuo"></i>
        </a-button>
      </a-tooltip>
    </div>
    <marker-modal
      :visible.sync="markerModealVisible"
      :title="markerModalTitle"
      :marker="currentMarker"
      :existingGroups="existingGroups"
      @confirm="handleConfirm"
      @delete="handleDeleteMarker"
      >
    </marker-modal>
    <location-picker
      :visible.sync="locationPickerVisible"
      @confirm="handleLocationResult">
    </location-picker>

    <import-export-modal
      :visible.sync="importExportModalVisible"
      :markerSetId="markerSet._id">
    </import-export-modal>

    <marker-search-modal
      :marker-list="markerList"
      :visible.sync="searchModalVisible"
      @markerClick="handleClickMarker"
    >
    </marker-search-modal>

    <location-link-analyzer
      :visible.sync="locationLinkAnalyzerVisible"
      @confirm="handleLocationResult">
    </location-link-analyzer>

    <polygon-modal
      :visible.sync="polygonModalVisible"
      :polygon="currentPolygon"
      :existingGroups="existingGroups"
      @confirm="handlePolygonConfirm"
      @delete="handleDeletePolygon">
    </polygon-modal>

  </div>
</template>

<script>
import MarkerModal from '@/components/marker-modal/index.vue';
import LocationPicker from '@/components/location-picker-2/index.vue';
import ImportExportModal from '@/components/import-export-modal/index.vue';
import MarkerSearchModal from '@/components/marker-search-modal/index.vue';
import LocationLinkAnalyzer from '@/components/location-link-analyzer/index.vue';
import PolygonModal from '@/components/polygon-modal/index.vue';
import pako from 'pako';
import {
  POLYGON_DEFAULT_COLOR_KEY,
  calcPolygonArea,
  formatArea,
  formatAreaMu,
} from '@/utils/polygon-utils';

export default {
  components: {
    MarkerModal,
    LocationPicker,
    ImportExportModal,
    MarkerSearchModal,
    LocationLinkAnalyzer,
    PolygonModal,
  },
  props: {
    id: String,
  },
  data() {
    return {
      markerList: [],
      lastMarkerList: [],
      markerSet: {},
      markerModealVisible: false,
      locationPickerVisible: false,
      markerModalTitle: '编辑标记',
      currentMarker: {},
      currentId: 0,
      loading: false,
      importExportModalVisible: false,
      searchModalVisible: false,
      savingLoading: false,
      locationLinkAnalyzerVisible: false,
      lastModifyTime: null,
      // 分组相关
      groupTabs: [], // ['全部', '分组A', '分组B', '未分组']
      activeGroup: '', // '' = 全部, '未分组' = 未分组, 其他 = 具体分组名
      filteredMarkerList: [], // 筛选后的标记列表（带 _realIndex 和 displaySeq）
      // 面标记相关
      polygonList: [],
      lastPolygonList: [],
      filteredPolygonList: [],
      currentPolygonId: 0,
      typeFilter: '', // '' = 点标记列表, 'polygon' = 面标记列表
      // 面编辑弹窗
      polygonModalVisible: false,
      currentPolygon: {},
      // 面绘制相关
      drawMode: '', // '' | 'polygon'
      drawPoints: [],
      drawColorKey: '',
      // 面积单位
      areaUnit: localStorage.getItem('areaUnit') || 'km', // 'km' (公制) | 'mu' (亩)
    };
  },
  methods: {
    getId() {
      // eslint-disable-next-line no-plusplus
      return ++this.currentId;
    },
    // ========== 分组相关 ==========
    // 刷新分组标签栏
    refreshGroupTabs(disableFit) {
      const list = this.markerList;
      const { polygonList } = this;
      const groupSet = new Set();
      list.forEach((m) => {
        if (m.group && m.group !== '未分组') groupSet.add(m.group);
      });
      polygonList.forEach((p) => {
        if (p.group && p.group !== '未分组') groupSet.add(p.group);
      });
      const groups = [...groupSet];

      let tabs = [];
      if (groups.length > 0) {
        tabs = ['全部', ...groups];
        const hasUngrouped = list.some(m => !m.group || m.group === '未分组')
          || polygonList.some(p => !p.group || p.group === '未分组');
        if (hasUngrouped) tabs.push('未分组');
      }

      // 若当前选中分组已不存在，重置为全部
      let { activeGroup } = this;
      if (activeGroup !== '' && activeGroup !== '未分组' && !groupSet.has(activeGroup)) {
        activeGroup = '';
      }
      if (groups.length === 0) activeGroup = '';

      this.groupTabs = tabs;
      this.activeGroup = activeGroup;
      this.applyGroupFilter(activeGroup, disableFit);
    },
    // 应用分组筛选
    applyGroupFilter(activeGroup, disableFit) {
      const list = this.markerList;
      let filtered;
      if (activeGroup === '') {
        // "全部"：不过滤
        filtered = list.map((m, i) => Object.assign({}, m, { displaySeq: i + 1, _realIndex: i }));
      } else if (activeGroup === '未分组') {
        // "未分组"：匹配 !m.group || m.group === '未分组'（兼容旧数据）
        let seq = 1;
        filtered = list
          .map((m, i) => Object.assign({}, m, { _realIndex: i }))
          .filter(m => !m.group || m.group === '未分组')
          .map((m) => {
            const r = Object.assign(m, { displaySeq: seq });
            seq += 1;
            return r;
          });
      } else {
        // 具体分组名：精确匹配
        let seq = 1;
        filtered = list
          .map((m, i) => Object.assign({}, m, { _realIndex: i }))
          .filter(m => m.group === activeGroup)
          .map((m) => {
            const r = Object.assign(m, { displaySeq: seq });
            seq += 1;
            return r;
          });
      }

      this.filteredMarkerList = filtered;

      // 同步筛选面标记
      const { polygonList } = this;
      let filteredPolygons;
      if (activeGroup === '') {
        filteredPolygons = polygonList.map((p, i) => Object.assign(
          {}, p, { _realIndex: i, displaySeq: i + 1 },
        ));
      } else if (activeGroup === '未分组') {
        let seq = 1;
        filteredPolygons = polygonList
          .map((p, i) => Object.assign({}, p, { _realIndex: i }))
          .filter(p => !p.group || p.group === '未分组')
          .map((p) => {
            const r = Object.assign(p, { displaySeq: seq });
            seq += 1;
            return r;
          });
      } else {
        let seq = 1;
        filteredPolygons = polygonList
          .map((p, i) => Object.assign({}, p, { _realIndex: i }))
          .filter(p => p.group === activeGroup)
          .map((p) => {
            const r = Object.assign(p, { displaySeq: seq });
            seq += 1;
            return r;
          });
      }
      this.filteredPolygonList = filteredPolygons;

      // 通知地图渲染筛选后的标记和面
      this.$emit('renderMarker', this.filteredMarkerList, disableFit);
      this.$emit('renderPolygon', this.filteredPolygonList, disableFit);

      // 自动切换类型筛选
      this._autoSwitchTypeFilter(filtered, filteredPolygons);

      return filtered;
    },
    // 自动切换点/面类型筛选
    _autoSwitchTypeFilter(filtered, filteredPolygons) {
      const hasMarkers = filtered.length > 0;
      const hasPolygons = filteredPolygons.length > 0;
      // 当前分组只有面标记时，自动切换到面列表
      if (!hasMarkers && hasPolygons) {
        this.typeFilter = 'polygon';
      } else if (hasMarkers && !hasPolygons) {
        this.typeFilter = '';
      }
      // 都有或都没有时，保持当前选择
    },
    // 点击分组标签
    handleGroupTabChange(key) {
      const activeGroup = key === '全部' ? '' : key;
      this.activeGroup = activeGroup;
      this.applyGroupFilter(activeGroup, false);
    },
    async initData() {
      this.loading = true;
      try {
        const res = await this.$api.proxy.markerListPageLoad({
          markerSetId: this.id,
          skipVisit: false,
        });
        // markerListPageLoad 返回格式：
        // { errCode, openid, markerSet, blackInfo, markerList, lastModifyTime }
        // markerList 是 { data: [], total: xxx } 格式
        // 注意：代理会将云函数返回值包装在 resp_data 字符串中，需 JSON.parse 解析
        const rawData = JSON.parse(res.data.data.resp_data);

        // 处理压缩响应（标记数 >= 500 时触发）
        let resultData;
        if (rawData.compressed && rawData.data) {
          // base64 解码 → pako.inflate 解压 → JSON.parse
          const binaryStr = atob(rawData.data);
          const charData = binaryStr.split('').map(c => c.charCodeAt(0));
          const inflated = pako.inflate(new Uint8Array(charData), { to: 'string' });
          resultData = JSON.parse(inflated);
        } else {
          resultData = rawData;
        }

        if (resultData.errCode !== undefined && resultData.errCode !== 0) {
          // 特定错误码处理
          if (resultData.errCode === 3) {
            this.$message.error('无权访问该标记集');
          } else if (resultData.errCode === 4) {
            this.$message.error('该标记集为邀请制，您尚未加入');
          } else {
            this.$message.error(resultData.errMsg || '加载失败');
          }
          this.loading = false;
          return;
        }
        // 设置标记集信息
        if (resultData.markerSet) {
          this.markerSet = resultData.markerSet;
          this.$bus.$emit('renderMarkerSet', resultData.markerSet);
        }
        // 设置标记列表
        if (resultData.markerList && resultData.markerList.data) {
          const markers = resultData.markerList.data;
          this.markerList = markers;
          this.lastMarkerList = JSON.parse(JSON.stringify(markers));
          // 设置id值
          this.markerList.forEach((marker) => {
            this.$set(marker, 'id', this.getId());
          });
        }
        // 按需加载面标记数据
        if (resultData.markerSet && resultData.markerSet.hasPolygon) {
          await this.loadPolygonData(resultData.markerSet._id);
        }
        // 刷新分组标签栏并渲染
        this.refreshGroupTabs();
        // 保存 lastModifyTime 用于后续保存时使用
        if (resultData.lastModifyTime) {
          this.lastModifyTime = resultData.lastModifyTime;
        }
      } catch (error) {
        console.error('markerListPageLoad error:', error);
        this.$message.error('加载标记数据失败');
      }
      this.loading = false;
    },
    handleClickHome() {
      this.$router.push('/main/marker-set');
    },
    handleClickAdd() {
      this.locationPickerVisible = true;
    },
    handleRightClickAdd() {
      // 直接在地图上画点
      this.$bus.$emit('prepareAddMarker');
    },
    handleClickEdit(marker) {
      this.markerModealVisible = true;
      this.currentMarker = marker;
    },
    handleDeleteMarker(marker) {
      const realIndex = this.markerList.findIndex(item => item.id === marker.id);
      if (realIndex !== -1) {
        this.markerList.splice(realIndex, 1);
      }
      this.refreshGroupTabs();
    },
    handleConfirm(marker) {
      const realIndex = this.markerList.findIndex(item => item.id === marker.id);
      if (realIndex !== -1) {
        this.$set(this.markerList, realIndex, marker);
      }
      this.refreshGroupTabs();
    },
    handleLocationResult(result, disableFit) {
      console.log('结果：', result);
      const data = {
        id: this.getId(),
        name: result.name,
        originName: result.name,
        address: result.address,
        labelContent: '',
        descContent: '',
        iconPath: `/common/img/${this.getDefaultMarkerIconName()}`,
        pictures: [],
        longitude: result.longitude,
        latitude: result.latitude,
        group: this.activeGroup || '', // 自动归属当前选中分组
      };
      // 添加到列表
      this.markerList.push(data);
      this.refreshGroupTabs(disableFit);
      this.$emit('markerClick', data);
    },
    handleItemClick(marker) {
      this.$emit('markerClick', marker);
    },
    showMarker(marker) {
      this.markerModealVisible = true;
      this.currentMarker = marker;
    },
    isArrayEq(arr1, arr2) {
      // eslint-disable-next-line no-param-reassign
      arr1 = arr1 || [];
      // eslint-disable-next-line no-param-reassign
      arr2 = arr2 || [];
      let flag = true;
      if (arr1.length !== arr2.length) {
        flag = false;
      } else {
        arr1.forEach((item) => {
          if (arr2.indexOf(item) === -1) {
            flag = false;
          }
        });
      }
      return flag;
    },
    getChangeData() {
      this.updateSeq();
      // 筛选新增
      const addList = this.markerList.filter(item => !item._id);
      // 筛选删除
      const currentObjectIdList = [];
      this.markerList.forEach((item2) => {
        if (item2._id) {
          currentObjectIdList.push(item2._id);
        }
      });
      const delList = this.lastMarkerList.filter((item) => {
        if (currentObjectIdList.indexOf(item._id) === -1) {
          return true;
        }
        return false;
      });
      // 筛选更新
      const updList = this.markerList.filter((item) => {
        const found = this.lastMarkerList.find(item2 => item2._id === item._id);
        // 判断图片有没有更新
        if (found) {
          if (found.seq !== item.seq
          || found.latitude !== item.latitude
          || found.longitude !== item.longitude
          || found.address !== item.address
          || found.name !== item.name
          || found.iconPath !== item.iconPath
          || found.labelContent !== item.labelContent
          || found.descContent !== item.descContent
          || (found.group || '') !== (item.group || '')
          || !this.isArrayEq(found.pictures, item.pictures)
          ) {
            return true;
          }
        }
        return false;
      });
      return {
        addList,
        delList,
        updList,
        saveList: addList.concat(updList),
      };
    },
    updateSeq() {
      this.markerList.forEach((item, index) => {
        this.$set(item, 'seq', index);
      });
    },
    getDefaultMarkerIconName() {
      const lastMarkerIcon = localStorage.getItem('lastMarkerIcon');
      return lastMarkerIcon || 'location1-1.png';
    },
    getCalloutObj(calloutName, isHighlight) {
      if (!calloutName) {
        return undefined;
      }
      return {
        content: calloutName,
        fontSize: 11,
        padding: 4,
        bgColor: '#000000',
        borderWidth: isHighlight ? 2 : 1,
        borderColor: isHighlight ? '#09bb07' : '#ffffff',
        color: '#ffffff',
        borderRadius: 5,
        display: 'ALWAYS',
      };
    },
    getLabelObj(labelName, isHighlight) {
      if (!labelName) {
        return undefined;
      }
      return {
        content: labelName,
        fontSize: 10,
        padding: 2,
        bgColor: '#ffffff',
        borderWidth: isHighlight ? 2 : 1,
        borderColor: isHighlight ? '#09bb07' : '#000000',
        borderRadius: 5,
      };
    },
    processData(list) {
      return list.map(item => ({
        id: new Date().getTime() + Math.floor(Math.random() * 1000),
        _id: item._id,
        markerSetId: this.markerSet._id,
        seq: item.seq,
        latitude: item.latitude,
        longitude: item.longitude,
        iconPath: item.iconPath,
        width: 20,
        height: 20,
        callout: this.getCalloutObj(item.name),
        labelContent: item.labelContent,
        label: this.getLabelObj(item.labelContent),
        descContent: item.descContent,
        name: item.name,
        originName: item.originName,
        address: item.address,
        pictures: item.pictures,
        group: item.group || '',
      }));
    },
    async handleSave() {
      const changeData = this.getChangeData();
      const polygonChangeData = this.getPolygonChangeData();
      const hasMarkerChanges = changeData.addList.length
        || changeData.delList.length
        || changeData.updList.length;
      const hasPolygonChanges = polygonChangeData.addList.length
        || polygonChangeData.delList.length
        || polygonChangeData.updList.length;

      if (!hasMarkerChanges && !hasPolygonChanges) {
        this.$message.success('保存成功');
        return;
      }
      try {
        this.savingLoading = true;

        // 先保存面标记
        if (hasPolygonChanges) {
          const polygonFinalData = {
            addList: this.processPolygonData(polygonChangeData.addList),
            delList: this.processPolygonData(polygonChangeData.delList),
            updList: this.processPolygonData(polygonChangeData.updList),
          };
          const polygonRes = await this.$api.proxy.markerPolygonUpdate(polygonFinalData);
          const polygonJsonResult = polygonRes.data.data.resp_data;
          if (polygonJsonResult.add) {
            polygonJsonResult.add.forEach((item, index) => {
              polygonChangeData.addList[index]._id = item._id;
            });
          }
          this.lastPolygonList = JSON.parse(JSON.stringify(this.polygonList));
        }

        // 再保存点标记
        if (hasMarkerChanges) {
          const finalData = {
            addList: this.processData(changeData.addList),
            delList: this.processData(changeData.delList),
            updList: this.processData(changeData.updList),
          };
          const res = await this.$api.proxy.markerListUpdate(finalData);
          const jsonResult = res.data.data.resp_data;
          if (jsonResult.add) {
            jsonResult.add.forEach((item, index) => {
              changeData.addList[index]._id = item._id;
            });
          }
          this.lastMarkerList = JSON.parse(JSON.stringify(this.markerList));
        }

        this.savingLoading = false;
        this.$message.success('保存成功');
      } catch (error) {
        this.savingLoading = false;
        console.error(error);
        this.$message.error('保存失败');
      }
    },
    handleLinkAdd() {
      this.locationLinkAnalyzerVisible = true;
    },
    handleClickMarker(marker) {
      this.$emit('markerClick', marker);
    },
    // ========== 面标记相关 ==========
    getPolygonId() {
      // eslint-disable-next-line no-plusplus
      return ++this.currentPolygonId;
    },
    async loadPolygonData(markerSetId) {
      try {
        const res = await this.$api.proxy.markerPolygonPageLoad({
          markerSetId,
          enableCompress: true,
        });
        const rawData = JSON.parse(res.data.data.resp_data);

        // 处理压缩响应（面标记数 >= 100 时触发）
        let resultData;
        if (rawData.compressed && rawData.data) {
          try {
            const binaryStr = atob(rawData.data);
            const charData = binaryStr.split('').map(c => c.charCodeAt(0));
            const inflated = pako.inflate(new Uint8Array(charData), { to: 'string' });
            resultData = JSON.parse(inflated);
          } catch (e) {
            console.error('解压面标记数据失败:', e);
            // 解压失败时返回明确错误，避免后续访问 undefined 字段
            resultData = {
              errCode: 5,
              errMsg: '面标记数据解压失败',
              polygonList: { data: [], total: 0 },
            };
          }
        } else {
          resultData = rawData;
        }

        // 错误码处理
        if (resultData.errCode !== undefined && resultData.errCode !== 0) {
          console.error('加载面标记失败:', resultData.errMsg);
          this.$message.error('加载面标记失败');
          return;
        }
        // polygonList 是 { data: [], total } 格式（与 markerList 一致）
        const polygons = (resultData.polygonList && resultData.polygonList.data) || [];
        if (polygons.length === 0) return;
        // 预计算面积
        const { areaUnit } = this;
        polygons.forEach((p) => {
          this.$set(p, 'id', this.getPolygonId());
          if (p.points && p.points.length >= 3) {
            if (!p._areaSqM) {
              // eslint-disable-next-line no-param-reassign
              p._areaSqM = calcPolygonArea(p.points);
            }
            // eslint-disable-next-line no-param-reassign
            p._area = areaUnit === 'mu'
              ? formatAreaMu(p._areaSqM)
              : formatArea(p._areaSqM);
          }
        });
        this.polygonList = polygons;
        this.lastPolygonList = JSON.parse(JSON.stringify(polygons));
      } catch (error) {
        console.error('loadPolygonData error:', error);
        this.$message.error('加载面标记失败');
      }
    },
    // 点/面类型切换
    handleTypeFilterChange(e) {
      this.typeFilter = e.target.value;
    },
    // 面标记编辑
    handleEditPolygon(polygon) {
      this.currentPolygon = polygon;
      this.polygonModalVisible = true;
    },
    handlePolygonConfirm(polygon) {
      const realIndex = this.polygonList.findIndex(item => item.id === polygon.id);
      if (realIndex !== -1) {
        // 更新面标记（保留 points 和 _area）
        const oldPolygon = this.polygonList[realIndex];
        Object.assign(oldPolygon, {
          name: polygon.name,
          colorKey: polygon.colorKey,
          group: polygon.group || '',
        });
      }
      this.refreshGroupTabs();
    },
    handleDeletePolygon(polygon) {
      const realIndex = this.polygonList.findIndex(item => item.id === polygon.id);
      if (realIndex !== -1) {
        this.polygonList.splice(realIndex, 1);
        // 更新 seq
        this.polygonList.forEach((item, i) => { this.$set(item, 'seq', i); });
      }
      this.refreshGroupTabs();
    },
    showPolygon(polygon) {
      // 地图上面标记被点击：打开编辑弹窗
      this.currentPolygon = polygon;
      this.polygonModalVisible = true;
    },
    // ========== 面标记绘制 ==========
    handleDrawPolygon() {
      this.drawMode = 'polygon';
      this.drawPoints = [];
      this.drawColorKey = localStorage.getItem('lastPolygonColorKey') || POLYGON_DEFAULT_COLOR_KEY;
      this.$bus.$emit('startDrawPolygon', this.drawColorKey);
    },
    handleDrawUndo() {
      if (this.drawPoints.length > 0) {
        this.drawPoints.pop();
        this.$bus.$emit('drawPointsUpdate', this.drawPoints, this.drawColorKey);
      }
    },
    handleDrawFinish() {
      if (this.drawPoints.length < 3) {
        this.$message.warning('至少需要3个点才能完成绘制');
        return;
      }
      // 生成默认名称
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      const defaultName = `面${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}`;

      const newPolygon = {
        id: this.getPolygonId(),
        markerSetId: this.markerSet._id,
        name: defaultName,
        points: this.drawPoints.slice(),
        colorKey: this.drawColorKey,
        group: this.activeGroup || '',
        seq: this.polygonList.length,
        _areaSqM: calcPolygonArea(this.drawPoints),
      };
      // 根据当前面积单位格式化 _area
      newPolygon._area = this.areaUnit === 'mu'
        ? formatAreaMu(newPolygon._areaSqM)
        : formatArea(newPolygon._areaSqM);

      this.polygonList.push(newPolygon);

      // 缓存颜色
      localStorage.setItem('lastPolygonColorKey', this.drawColorKey);

      // 清除绘制状态
      this.drawMode = '';
      this.drawPoints = [];
      this.drawColorKey = '';
      this.$bus.$emit('endDrawPolygon');

      // 刷新
      this.refreshGroupTabs();
      // 自动切换到面列表
      this.typeFilter = 'polygon';

      this.$message.success('面标记已添加');
    },
    handleDrawCancel() {
      this.drawMode = '';
      this.drawPoints = [];
      this.drawColorKey = '';
      this.$bus.$emit('endDrawPolygon');
    },
    handleDrawColorChange(colorKey) {
      this.drawColorKey = colorKey;
      this.$bus.$emit('drawPointsUpdate', this.drawPoints, this.drawColorKey);
    },
    // 切换面积单位（公制 ↔ 亩）
    handleToggleAreaUnit() {
      const newUnit = this.areaUnit === 'km' ? 'mu' : 'km';
      // 重新格式化所有面标记的面积
      this.polygonList.forEach((p) => {
        if (p._areaSqM) {
          this.$set(p, '_area', newUnit === 'mu'
            ? formatAreaMu(p._areaSqM)
            : formatArea(p._areaSqM));
        }
      });
      this.areaUnit = newUnit;
      // 持久化单位偏好
      localStorage.setItem('areaUnit', newUnit);
      // 重新过滤刷新列表（filteredPolygonList 是浅拷贝，直接改 polygonList 不会触发更新）
      this.applyGroupFilter(this.activeGroup, true);
    },
    // 面标记变更检测
    getPolygonChangeData() {
      const addList = this.polygonList.filter(p => !p._id);
      const currentIds = new Set(this.polygonList.filter(p => p._id).map(p => p._id));
      const delList = this.lastPolygonList.filter(p => p._id && !currentIds.has(p._id));
      const updList = this.polygonList.filter((p) => {
        if (!p._id) return false;
        const found = this.lastPolygonList.find(p2 => p2._id === p._id);
        if (!found) return false;
        return found.name !== p.name
          || found.colorKey !== p.colorKey
          || (found.group || '') !== (p.group || '')
          || found.seq !== p.seq
          || JSON.stringify(found.points) !== JSON.stringify(p.points);
      });
      return { addList, delList, updList };
    },
    processPolygonData(list) {
      return list.map((p) => {
        const item = Object.assign({}, p);
        // 排除前端计算字段
        delete item._area;
        delete item._areaSqM;
        delete item.id;
        item.markerSetId = this.markerSet._id;
        item.group = item.group || '';
        return item;
      });
    },
  },
  async created() {
    await this.initData();
  },
  computed: {
    existingGroups() {
      const groups = [
        ...this.markerList.map(m => m.group),
        ...this.polygonList.map(p => p.group),
      ];
      return [...new Set(groups.filter(g => g && g !== '未分组'))];
    },
    // a-tabs activeKey 用字符串，'' 表示"全部"
    activeGroupKey() {
      return this.activeGroup === '' ? '全部' : this.activeGroup;
    },
    // 当前分组是否同时有点和面
    hasMixedTypes() {
      return this.filteredMarkerList.length > 0 && this.filteredPolygonList.length > 0;
    },
    // 当前分组是否只有面标记
    hasOnlyPolygons() {
      return this.filteredMarkerList.length === 0 && this.filteredPolygonList.length > 0;
    },
  },
  mounted() {
    this.$bus.$on('markerAdd', (param) => {
      this.handleLocationResult(param, true);
    });
    // 地图绘制模式事件
    this.$bus.$on('drawAddPointResult', (center) => {
      this.drawPoints.push({ latitude: center.latitude, longitude: center.longitude });
      this.$bus.$emit('drawPointsUpdate', this.drawPoints, this.drawColorKey);
    });
    // 绘制工具栏按钮事件
    this.$bus.$on('drawToolbarUndo', this.handleDrawUndo);
    this.$bus.$on('drawToolbarFinish', this.handleDrawFinish);
    this.$bus.$on('drawToolbarCancel', this.handleDrawCancel);
    this.$bus.$on('drawToolbarColorChange', this.handleDrawColorChange);
  },
  watch: {
    // 编辑弹窗关闭时取消高亮
    markerModealVisible(val) {
      if (!val) this.$bus.$emit('resetMarkerHighlight');
    },
    polygonModalVisible(val) {
      if (!val) this.$bus.$emit('resetPolygonHighlight');
    },
  },
  destroyed() {
    this.$bus.$off('markerAdd');
    this.$bus.$off('drawAddPointResult');
    this.$bus.$off('drawToolbarUndo', this.handleDrawUndo);
    this.$bus.$off('drawToolbarFinish', this.handleDrawFinish);
    this.$bus.$off('drawToolbarCancel', this.handleDrawCancel);
    this.$bus.$off('drawToolbarColorChange', this.handleDrawColorChange);
  },
};
</script>

<style scoped>

.page-layout {
  display: flex;
  height: 100vh;
}

.page-layout__card {
  flex: 1;
  min-width: 0;
}

* /deep/ .marker-list {
  flex: 1;
  overflow: hidden;
  width: calc(100% + 24px);
}

* /deep/ .marker-list__list {
  height: 100%;
  overflow-y: auto;
}

.marker-list__tools {
  flex: 0 0 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 15px;
  background-color: #fff;
  border-right: 1px solid #e8e8e8;
}

.marker-list__tools > * {
  margin-bottom: 15px;
}

* /deep/ .ant-list-item-action {
  padding-right: 10px;
  margin-left: 10px;
}

.title-bar {
  display: flex;
}

.title-name {
  flex: 1;
  text-align: center;
  line-height: 32px;
}

.btn-add {
  float: right;
}
* /deep/ .ant-list-item:hover {
  cursor: pointer;
  /* background-color: #e6f7ff; */
}

.marker-no {
  display: inline-block;
  width: 20px;
  height: 20px;
  text-align: center;
  line-height: 20px;
  border: 1px solid #eaeaea;
  border-radius: 50%;
  color: #aaaaaa;
  margin-right: 10px;
}

.ant-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

* /deep/ .ant-card-head {
  padding-right: 48px;
}

* /deep/ .ant-card-body {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.type-filter {
  margin: 0 0 8px;
  padding: 8px 0 4px 0;
}

.type-filter__group {
  display: flex;
  width: 100%;
}

.type-filter__group .ant-radio-button-wrapper {
  flex: 1;
  text-align: center;
  border-radius: 0;
}

.polygon-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  background-color: #eaeaea;
  flex-shrink: 0;
}

.polygon-no__inner {
  width: 18px;
  height: 18px;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  background-color: #ffffff;
  text-align: center;
  line-height: 18px;
  color: #aaaaaa;
  font-size: 12px;
}

.area-toggle {
  color: #52c41a;
  cursor: pointer;
}

.area-toggle:hover {
  color: #73d13d;
}

.group-tabs {
  margin: 0 -24px;
}

* /deep/ .ant-tabs-bar {
  margin-bottom: 0;
}

</style>
